import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, auth } from './firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import Logo from './components/Logo';
import TalentAnalyticsDashboard from './TalentAnalyticsDashboard';
import './LinkedInTheme.css';

function SharedTalentPool() {
  const { t } = useTranslation();
  const { orgId: orgIdFromParams } = useParams();
  const [sharedVtcs, setSharedVtcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [isListVisible, setIsListVisible] = useState(false);
  const [internalUserIds, setInternalUserIds] = useState(new Set());

  useEffect(() => {
    const fetchSharedVtcs = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error(t('sharedTalentPool.loginRequired'));
        }

        let orgIdToQuery = orgIdFromParams;

        if (!orgIdToQuery) {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (!userDoc.exists() || !userDoc.data().profile?.orgId) {
            throw new Error(t('sharedTalentPool.orgAssociationRequired'));
          }
          orgIdToQuery = userDoc.data().profile.orgId;
        }

        const orgDocRef = doc(db, 'organizations', orgIdToQuery);
        const orgDoc = await getDoc(orgDocRef);
        if (orgDoc.exists()) {
          setOrganizationName(orgDoc.data().name);
        }

        // Source 1: Get internal VTCs and User IDs from employees
        const usersQuery = query(collection(db, 'users'), where('profile.orgId', '==', orgIdToQuery));
        const usersSnapshot = await getDocs(usersQuery);
        const internalVtcIds = usersSnapshot.docs
          .map(doc => doc.data().profile?.internalVtcId)
          .filter(Boolean);
        setInternalUserIds(new Set(usersSnapshot.docs.map(doc => doc.id)));

        // Source 2: Get externally shared VTCs
        const sharedVtcQuery = query(collection(db, 'sharedVTCs'), where('orgId', '==', orgIdToQuery));
        const sharedVtcSnapshot = await getDocs(sharedVtcQuery);
        const sharedVtcIds = sharedVtcSnapshot.docs.map(doc => doc.data().vtcId);

        // Combine and deduplicate VTC IDs
        const allVtcIds = [...new Set([...internalVtcIds, ...sharedVtcIds])];

        if (allVtcIds.length === 0) {
          setSharedVtcs([]);
          setLoading(false);
          return;
        }

        const talentCardPromises = allVtcIds.map(id => getDoc(doc(db, 'talentCards', id)));
        const talentCardDocs = await Promise.all(talentCardPromises);
        
        const talentCards = talentCardDocs
          .filter(doc => doc.exists())
          .map(doc => ({ id: doc.id, ...doc.data() }));

        setSharedVtcs(talentCards);
      } catch (err) {
        console.error("Error fetching shared VTCs:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedVtcs();
  }, [orgIdFromParams, t]);

  if (loading) {
    return (
      <div className="li-layout" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <Logo size={48} className="loading-logo" style={{ justifyContent: 'center' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="li-layout">
         <div className="container error-message" style={{ marginTop: '20px' }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="li-layout" style={{ display: 'block' }}>
      <div className="container" style={{ maxWidth: '1400px', marginTop: '24px' }}>
        <header className="page-header" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: 'rgba(0,0,0,0.9)', margin: 0 }}>
            {organizationName} {t('sharedTalentPool.talentIntelligence')}
          </h1>
          <button className="li-btn-outline-primary" onClick={() => setIsListVisible(!isListVisible)} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isListVisible ? (
              <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" width="20" height="20"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>
            ) : (
              <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" width="20" height="20"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            )}
            <span>{isListVisible ? t('sharedTalentPool.hideTalentPool') : t('sharedTalentPool.viewTalentPool')}</span>
          </button>
        </header>

        <div className="li-card li-feed-post" style={{ marginBottom: '24px' }}>
             <div className="li-card-header">
                <h3>{t('sharedTalentPool.analyticsOverview')}</h3>
             </div>
             <div className="li-card-body">
                 <TalentAnalyticsDashboard talentData={sharedVtcs} />
             </div>
        </div>

        {isListVisible && (
          <div className="talent-pool-modal-overlay" onClick={() => setIsListVisible(false)}>
            <div className="talent-pool-modal-content" onClick={e => e.stopPropagation()}>
              <div className="talent-pool-modal-header">
                <h3 className="card-title">{t('sharedTalentPool.talentPool')} ({sharedVtcs.length} {t('sharedTalentPool.candidates')})</h3>
                <button className="close-button" onClick={() => setIsListVisible(false)}>&times;</button>
              </div>
              <div className="talent-pool-modal-body">
                {sharedVtcs.length === 0 ? (
                  <p>{t('sharedTalentPool.noTalentCardsShared')}</p>
                ) : (
                  sharedVtcs.map(card => (
                    <div key={card.id} className="talent-list-item">
                      <div className="talent-info">
                        <h4>
                          {card.name} - {card.title}
                          {internalUserIds.has(card.userId) ? (
                            <span className="internal-badge" style={{marginLeft: '10px'}}>{t('sharedTalentPool.internal')}</span>
                          ) : (
                            <span className="external-badge" style={{marginLeft: '10px'}}>{t('sharedTalentPool.external')}</span>
                          )}
                        </h4>
                        <div className="skills-list">
                          {card.skills?.slice(0, 5).map((skill, i) => <span key={i} className="skill-tag">{skill}</span>)}
                        </div>
                      </div>
                      <div className="talent-pool-action">
                        <Link to={`/talent-card/${card.id}`} className="icon-btn">
                          <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <footer style={{ marginTop: '40px', padding: '24px', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
         <Link to="/about" style={{ margin: '0 12px', color: '#666', textDecoration: 'none' }}>{t('footer.about')}</Link>
         <Link to="/accessibility" style={{ margin: '0 12px', color: '#666', textDecoration: 'none' }}>{t('footer.accessibility')}</Link>
         <Link to="/help-center" style={{ margin: '0 12px', color: '#666', textDecoration: 'none' }}>{t('footer.helpCenter')}</Link>
         <Link to="/privacy-terms" style={{ margin: '0 12px', color: '#666', textDecoration: 'none' }}>{t('footer.privacyTerms')}</Link>
         <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>{t('auth.footer.rights')}</div>
      </footer>
    </div>
  );
}

export default SharedTalentPool;